import { useEffect, useRef, useState } from 'react';
import {
    IconAlignCenter,
    IconAlignJustified,
    IconAlignLeft,
    IconAlignRight,
    IconBold,
    IconCheck,
    IconChevronDown,
    IconItalic,
    IconPaint,
    IconPlus,
    IconUnderline,
} from '@tabler/icons-react';
import { COLOR_SWATCHES, DEFAULT_TEXT_COLOR, FONT_SIZES, LINE_HEIGHTS, QUICK_COLORS } from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { Popover } from '../ui/prims';
import Hint from '../../../components/common/Hint';
import FontPicker from './FontPicker';
import { useFormatting } from './useFormatting';

/** The heading presets the style gallery offers, biggest first. */
const STYLES = [
    { id: 'heading', fontSize: 24, bold: true, previewSize: 15 },
    { id: 'subheading', fontSize: 16, bold: true, previewSize: 12.5 },
    { id: 'body', fontSize: 11, bold: false, previewSize: 11 },
];

/**
 * The −/value/+ control the ribbon uses for a number picked off a short list.
 *
 * A stepper rather than a dropdown: nudging a size or the line spacing one
 * notch is what these are for, and the full list is still in the inspector.
 */
function Stepper({ value, steps, onChange, format, labelDown, labelUp, width = 34 }) {
    const step = (delta) => {
        const sorted = steps.includes(value) ? steps : [...steps, value].sort((a, b) => a - b);
        const next = sorted[Math.min(sorted.length - 1, Math.max(0, sorted.indexOf(value) + delta))];
        if (next && next !== value) onChange(next);
    };
    const arrow = 'h-full w-6 text-[15px] leading-none text-[#6B635C] transition-colors hover:text-[#1C1815] dark:text-zinc-400';
    return (
        <div className="flex h-7 flex-none items-center rounded-[7px] border border-[#E2DCD6] bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <Hint label={labelDown}>
                <button type="button" onClick={() => step(-1)} aria-label={labelDown} className={arrow}>−</button>
            </Hint>
            <span
                className="text-center text-[12.5px] font-semibold tabular-nums text-[#1C1815] dark:text-zinc-100"
                style={{ width }}
            >
                {format ? format(value) : value}
            </span>
            <Hint label={labelUp}>
                <button type="button" onClick={() => step(1)} aria-label={labelUp} className={arrow}>+</button>
            </Hint>
        </div>
    );
}

/** A 28px square toggle, the ribbon's unit for anything on-or-off. */
function Toggle({ icon: Icon, label, active, onClick }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                aria-label={label}
                aria-pressed={!!active}
                className={`flex h-8 w-8 flex-none items-center justify-center rounded-[8px] transition-colors ${
                    active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-[#3D3833] hover:bg-[#F0ECE8] dark:text-zinc-200 dark:hover:bg-zinc-800'
                }`}
            >
                <Icon size={19} stroke={1.9} />
            </button>
        </Hint>
    );
}

const same = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const normHex = (value) => {
    const m = HEX_RE.exec(String(value || '').trim());
    if (!m) return null;
    const hex = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
    return `#${hex.toUpperCase()}`;
};

// Colours picked with the custom picker this session, newest first, shared by
// every colour menu — so a brand colour set once is one click the next time.
let recentCustom = [];
const rememberCustom = (color) => {
    recentCustom = [color, ...recentCustom.filter((c) => !same(c, color))].slice(0, 6);
};

/**
 * The browser's own colour picker, applied once the user settles on a colour
 * (`change`), not on every drag step (`input`) — each apply is an undo step.
 */
function CustomColorInput({ inputRef, value, onPick }) {
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return undefined;
        const onChange = () => onPick(normHex(el.value));
        el.addEventListener('change', onChange);
        return () => el.removeEventListener('change', onChange);
    }, [inputRef, onPick]);
    return (
        <input
            ref={inputRef}
            type="color"
            tabIndex={-1}
            aria-hidden
            defaultValue={normHex(value) || '#000000'}
            className="pointer-events-none absolute h-0 w-0 opacity-0"
        />
    );
}

/** One square in the menu's grids, ticked when it is the current colour. */
function Swatch({ color, active, label, onClick, size = 24 }) {
    const light = ['#FFFFFF', '#FFF', 'white'].some((c) => same(c, color));
    return (
        <Hint label={label || color}>
            <button
                type="button"
                onClick={onClick}
                aria-label={label || color}
                aria-pressed={active}
                style={{ background: color, width: size, height: size }}
                className={`relative flex flex-none items-center justify-center rounded-md border transition-transform hover:scale-110 ${
                    active ? 'border-primary ring-2 ring-primary/30' : 'border-black/10 dark:border-white/15'
                }`}
            >
                {active ? <IconCheck size={13} stroke={3} className={light ? 'text-[#1C1815]' : 'text-white'} /> : null}
            </button>
        </Hint>
    );
}

/**
 * Word's split colour control: the button says what the colour IS (an "A" —
 * or a paint bucket for the fill — over a bar of the current colour), the
 * menu says what you can pick, in labelled sections: the default, the preset
 * colours, and your own custom colour.
 *
 * `kind` is 'text' (a Default row) or 'fill' (a "No fill" row instead).
 */
function ColorMenu({ kind, label, value, onChange }) {
    const { t } = useTranslation();
    const ref = useRef(null);
    const pickerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [hex, setHex] = useState('');
    const fill = kind === 'fill';
    const none = !value || value === 'transparent';

    useEffect(() => {
        if (open) setHex(none ? '' : (normHex(value) || ''));
    }, [open, value, none]);

    const pick = (color) => {
        if (!color) return;
        onChange(color);
        setOpen(false);
    };
    const pickCustom = (color) => {
        if (!color) return;
        rememberCustom(color);
        pick(color);
    };
    const isPreset = COLOR_SWATCHES.some((c) => same(c, value)) || same(value, DEFAULT_TEXT_COLOR);

    return (
        <>
            <Hint label={label}>
                <button
                    ref={ref}
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-label={label}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    className={`flex h-8 flex-none items-center gap-0.5 rounded-[8px] pl-1.5 pr-1 transition-colors ${
                        open ? 'bg-[#F0ECE8] dark:bg-zinc-800' : 'hover:bg-[#F0ECE8] dark:hover:bg-zinc-800'
                    }`}
                >
                    <span className="flex w-[20px] flex-col items-center">
                        {fill ? (
                            <IconPaint size={16} stroke={1.9} className="text-[#3D3833] dark:text-zinc-200" />
                        ) : (
                            <span className="font-serif text-[16px] font-bold leading-[16px] text-[#3D3833] dark:text-zinc-200">A</span>
                        )}
                        {/* The current colour as a bar; "none" is a red slash on white. */}
                        <span
                            className="relative mt-[2px] h-[5px] w-full overflow-hidden rounded-[2px] border border-black/10 dark:border-white/15"
                            style={{ background: none ? '#FFFFFF' : value }}
                        >
                            {none ? <span className="absolute left-1/2 top-1/2 h-px w-7 -translate-x-1/2 -translate-y-1/2 -rotate-12 bg-[#D33A2C]" /> : null}
                        </span>
                    </span>
                    <IconChevronDown size={12} stroke={2.2} className="text-[#8A827B]" />
                </button>
            </Hint>

            <Popover anchorRef={ref} open={open} onClose={() => setOpen(false)} width={236}>
                <div className="p-1.5">
                    <div className="mb-1.5 px-0.5 text-[12px] font-semibold text-[#1C1815] dark:text-zinc-100">{label}</div>

                    {/* The one-click way back: the editor's default ink, or no fill. */}
                    <button
                        type="button"
                        onClick={() => pick(fill ? 'transparent' : DEFAULT_TEXT_COLOR)}
                        className={`mb-2 flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-[12.5px] font-medium transition-colors ${
                            (fill ? none : same(value, DEFAULT_TEXT_COLOR))
                                ? 'border-primary/50 bg-primary-50 text-primary-700'
                                : 'border-[#ECE8E4] text-[#3D3833] hover:bg-[#F7F5F2] dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800'
                        }`}
                    >
                        <span
                            className="relative h-4 w-4 flex-none overflow-hidden rounded-[4px] border border-black/15"
                            style={{ background: fill ? '#FFFFFF' : DEFAULT_TEXT_COLOR }}
                        >
                            {fill ? <span className="absolute left-1/2 top-1/2 h-px w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#D33A2C]" /> : null}
                        </span>
                        <span className="flex-1">{fill ? t('pdfEditor.colorMenu.noFill') : t('pdfEditor.colorMenu.default')}</span>
                        {(fill ? none : same(value, DEFAULT_TEXT_COLOR)) ? <IconCheck size={14} stroke={2.6} /> : null}
                    </button>

                    <div className="mb-1 px-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.colorMenu.presets')}
                    </div>
                    <div className="mb-2 grid grid-cols-6 gap-1.5 px-0.5">
                        {COLOR_SWATCHES.map((c) => (
                            <Swatch key={c} color={c} active={same(c, value)} onClick={() => pick(c)} />
                        ))}
                    </div>

                    <div className="mb-1 px-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.colorMenu.custom')}
                    </div>
                    <div className="mb-2 flex flex-wrap items-center gap-1.5 px-0.5">
                        {/* The colour in use when it is not one of the presets — a
                            colour read off the PDF, say — so it is never invisible. */}
                        {!none && !isPreset && !recentCustom.some((c) => same(c, value)) ? (
                            <Swatch color={value} active onClick={() => pick(value)} />
                        ) : null}
                        {recentCustom.map((c) => (
                            <Swatch key={c} color={c} active={same(c, value)} onClick={() => pick(c)} />
                        ))}
                        <Hint label={t('pdfEditor.colorMenu.pick')}>
                            <button
                                type="button"
                                onClick={() => {
                                    const el = pickerRef.current;
                                    if (!el) return;
                                    el.value = normHex(value) || '#000000';
                                    el.click();
                                }}
                                aria-label={t('pdfEditor.colorMenu.pick')}
                                className="flex h-6 w-6 flex-none items-center justify-center rounded-md border border-dashed border-[#B8B1AA] text-[#6B635C] transition-colors hover:border-primary hover:text-primary dark:border-zinc-600 dark:text-zinc-300"
                                style={{ background: 'conic-gradient(from 0deg, #ff5f5f22, #ffd25f22, #5fff8a22, #5fc8ff22, #b45fff22, #ff5f5f22)' }}
                            >
                                <IconPlus size={14} stroke={2.4} />
                            </button>
                        </Hint>
                        <CustomColorInput inputRef={pickerRef} value={value} onPick={pickCustom} />
                    </div>

                    {/* Or type it: the exact brand hex beats eyeballing a picker. */}
                    <form
                        className="flex items-center gap-1.5 px-0.5"
                        onSubmit={(e) => { e.preventDefault(); pickCustom(normHex(hex)); }}
                    >
                        <span
                            className="h-7 w-7 flex-none rounded-md border border-black/10"
                            style={{ background: normHex(hex) || '#FFFFFF' }}
                        />
                        <input
                            value={hex}
                            onChange={(e) => setHex(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="#1C1815"
                            spellCheck={false}
                            aria-label={t('pdfEditor.colorMenu.hex')}
                            className="h-7 min-w-0 flex-1 rounded-md border border-[#ECE8E4] bg-white px-2 font-mono text-[12px] uppercase text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                        <button
                            type="submit"
                            disabled={!normHex(hex)}
                            className="h-7 flex-none rounded-md bg-primary px-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-40"
                        >
                            {t('common.apply')}
                        </button>
                    </form>
                </div>
            </Popover>
        </>
    );
}

/**
 * The ribbon's font, paragraph and style groups.
 *
 * They are the reason the ribbon has a Page tab at all: the things you reach for
 * while writing, in the open rather than behind a selection. Everything here
 * goes through the same `write` as the inspector, so pressing Bold with three
 * words highlighted still marks only those three.
 *
 * `Group` comes from the ribbon so the columns and their captions match the rest
 * of the row exactly.
 */
export default function RibbonTextGroups({ Group, groupLabel }) {
    const { t } = useTranslation();
    const { write, textStyle, toolSettings } = useFormatting();

    // Only another kind of object — a shape, an image — leaves these groups
    // nothing to format. They stay in place so the tab does not reflow, but
    // read as unavailable; they still show the text defaults rather than
    // emptying out, so a greyed group looks disabled and not broken.
    const off = !textStyle;
    const s = textStyle || toolSettings.text;

    const size = Number(s.fontSize) || 12;

    const ALIGN = [
        { value: 'left', icon: IconAlignLeft, label: t('pdfEditor.options.alignLeft') },
        { value: 'center', icon: IconAlignCenter, label: t('pdfEditor.options.alignCenter') },
        { value: 'right', icon: IconAlignRight, label: t('pdfEditor.options.alignRight') },
        { value: 'justify', icon: IconAlignJustified, label: t('pdfEditor.options.alignJustify') },
    ];

    return (
        <div className={`flex items-stretch ${off ? 'pointer-events-none opacity-40' : ''}`}>
            <Group label={groupLabel('font')}>
                <div className="flex flex-col justify-center gap-[5px]">
                    <div className="flex items-center gap-[5px]">
                        <div className="w-[146px]">
                            <FontPicker
                                value={s.fontFamily}
                                bold={s.bold}
                                italic={s.italic}
                                // A box made by "Edit text" is set in the PDF's
                                // own embedded face, which is in no catalogue —
                                // name it, so the picker says what is on the page.
                                label={s.fontStack ? (s.fontLabel || t('pdfEditor.options.documentFont')) : s.fontFamily}
                                onChange={(v) => write('text', { fontFamily: v, fontStack: null, fontLabel: null })}
                            />
                        </div>
                        <Stepper
                            value={size}
                            steps={FONT_SIZES}
                            onChange={(v) => write('text', { fontSize: v })}
                            format={(v) => Math.round(v * 10) / 10}
                            labelDown={t('pdfEditor.options.sizeDown')}
                            labelUp={t('pdfEditor.options.sizeUp')}
                        />
                    </div>

                    <div className="flex items-center gap-[5px]">
                        <div className="flex items-center gap-0.5">
                            <Toggle icon={IconBold} label={t('pdfEditor.options.bold')} active={!!s.bold} onClick={() => write('text', { bold: !s.bold })} />
                            <Toggle icon={IconItalic} label={t('pdfEditor.options.italic')} active={!!s.italic} onClick={() => write('text', { italic: !s.italic })} />
                            <Toggle icon={IconUnderline} label={t('pdfEditor.options.underline')} active={!!s.underline} onClick={() => write('text', { underline: !s.underline })} />
                        </div>
                        <span className="h-5 w-px flex-none bg-[#F0ECE8] dark:bg-zinc-800" />
                        <ColorMenu
                            kind="text"
                            label={t('pdfEditor.colorMenu.textTitle')}
                            value={s.color}
                            onChange={(c) => write('text', { color: c })}
                        />
                        {/* The four everyone reaches for, one click instead of
                            three — round, so they read as shortcuts and not as
                            the colour in use, which the "A" above its bar shows. */}
                        <div className="flex items-center gap-1">
                            {QUICK_COLORS.map(({ color, name }) => {
                                const active = same(s.color, color);
                                const label = t(`pdfEditor.colorMenu.names.${name}`);
                                return (
                                    <Hint key={color} label={label}>
                                        <button
                                            type="button"
                                            onClick={() => write('text', { color })}
                                            aria-label={label}
                                            aria-pressed={active}
                                            className={`flex h-[24px] w-[24px] flex-none items-center justify-center rounded-full transition-transform hover:scale-110 ${
                                                active ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-zinc-900' : ''
                                            }`}
                                        >
                                            <span
                                                className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-black/10"
                                                style={{ background: color }}
                                            >
                                                {active ? <IconCheck size={11} stroke={3.2} className="text-white" /> : null}
                                            </span>
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Group>

            <Group label={groupLabel('paragraph')}>
                <div className="flex flex-col justify-center gap-[5px]">
                    <div className="flex items-center gap-1">
                        {ALIGN.map((a) => (
                            <Toggle
                                key={a.value}
                                icon={a.icon}
                                label={a.label}
                                active={(s.align || 'left') === a.value}
                                onClick={() => write('text', { align: a.value })}
                            />
                        ))}
                    </div>

                    {/* The mock puts list buttons here. A text object has no
                        list model, so this row carries the two paragraph
                        properties it really has — and which nothing in the
                        editor has ever let you set. */}
                    <div className="flex items-center gap-[5px]">
                        <Stepper
                            value={Number(s.lineHeight) || 1.35}
                            steps={LINE_HEIGHTS}
                            onChange={(v) => write('text', { lineHeight: v })}
                            format={(v) => v.toFixed(2).replace(/0$/, '')}
                            labelDown={t('pdfEditor.options.lineHeightDown')}
                            labelUp={t('pdfEditor.options.lineHeightUp')}
                            width={38}
                        />
                        <span className="h-5 w-px flex-none bg-[#F0ECE8] dark:bg-zinc-800" />
                        <ColorMenu
                            kind="fill"
                            label={t('pdfEditor.colorMenu.fillTitle')}
                            value={s.background}
                            onChange={(c) => write('text', { background: c })}
                        />
                    </div>
                </div>
            </Group>

            <Group label={groupLabel('styles')} last>
                <div className="flex items-center gap-1.5">
                    {STYLES.map((st) => (
                        <button
                            key={st.id}
                            type="button"
                            onClick={() => write('text', { fontSize: st.fontSize, bold: st.bold })}
                            // The tile is the preview: the name is set in the
                            // style it applies, which says more than a label.
                            className={`flex h-[74px] w-[84px] flex-none flex-col items-center justify-center gap-[2px] rounded-[10px] px-1 transition-colors ${
                                size === st.fontSize && !!s.bold === st.bold
                                    ? 'bg-primary-50'
                                    : 'hover:bg-[#F0ECE8] dark:hover:bg-zinc-800'
                            }`}
                        >
                            <span
                                className="max-w-full truncate py-px leading-[1.5] text-[#1C1815] dark:text-zinc-100"
                                style={{ fontSize: st.previewSize, fontWeight: st.bold ? 700 : 400 }}
                            >
                                {t(`pdfEditor.styles.${st.id}`)}
                            </span>
                            <span className="text-[10px] text-[#A8A099]">{st.fontSize}pt</span>
                        </button>
                    ))}
                </div>
            </Group>
        </div>
    );
}
